import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { Tabs, Tab, List, ListSubheader } from '@material-ui/core';
import { useDragLayer } from 'react-dnd';
import CredentialRow, { CredentialDragPreview } from './CredentialRow';

import FormSection from '../../form/FormSection';
import NgpFormButton from '../../form/NgpFormButton';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import { usePollingStyles } from './PollingStyles';
import { CREDENTIAL_TYPES, SNMPV3_PROTOCOLS, SNMPV3_PRIVACY_PROTOCOLS } from './PollingConstants';
import { fillCredPass } from './CredentialHelper';
import CredentialDialog from './CredentialDialog';

const TAB_SNMP = 'snmp';
const TAB_WINRM = 'winmrm';
const TAB_API = 'api';
const CRED_TABS_META = {
    [TAB_SNMP]: {
        label: 'SNMP',
        types: ['SNMP v2c', 'SNMP v3'],
        defaultType: 'SNMP v2c',
    },
    [TAB_WINRM]: {
        label: 'WinRM',
        types: ['WinRM', 'WinRM Cert'],
        defaultType: 'WinRM',
    },
    [TAB_API]: {
        label: 'API',
        types: ['REST'],
        defaultType: 'REST',
    },
};
function getDefaultCredentialType(tab) {
    const meta = CRED_TABS_META[tab] || CRED_TABS_META[TAB_SNMP];
    return meta.defaultType;
}

export default function CredentialList({ credentials, onChange, hasWinrmCert = false }) {
    const [selectedCredentialTab, setSelectedCredentialTab] = useState(TAB_SNMP);
    const handleTabChange = (event, newValue) => {
        setSelectedCredentialTab(newValue);
    };
    const [showDialog, setShowDialog] = useState(false);
    const [editingCredential, setEditingCredential] = useState(null);
    const { isDragActive } = useDragLayer((monitor) => ({
        isDragActive: monitor.isDragging(),
    }));
    const visibleCredentials = useMemo(
        () =>
            credentials
                .map((cred, index) => ({
                    cred,
                    index,
                }))
                .filter((item) => _.includes(CRED_TABS_META[selectedCredentialTab].types, item.cred.type)),
        [credentials, selectedCredentialTab]
    );

    const classes = usePollingStyles();

    const moveItem = useCallback(
        (from, to) => {
            const items = [...credentials];

            const [removed] = items.splice(from, 1);
            items.splice(to, 0, removed);

            onChange(items);
        },
        [credentials, onChange]
    );

    const deleteCredential = useCallback(
        (index) => {
            const newList = credentials.filter((credItem, i) => i !== index);
            onChange(newList);
        },
        [credentials, onChange]
    );

    const handleEditCredential = (credential, index) => {
        // const index = credentials.indexOf(credential);
        const isNew = !credential;
        const isUnsaved = !isNew && !credential._id;

        const currentCredentials = credentials.filter((c) => c !== credential);

        const credentialTypes = hasWinrmCert
            ? CREDENTIAL_TYPES
            : CREDENTIAL_TYPES.filter((item) => item !== 'WinRM Cert');

        const initialCredential = isNew
            ? {
                  type: getDefaultCredentialType(selectedCredentialTab),
                  protocol: SNMPV3_PROTOCOLS[0],
                  privacyProtocol: SNMPV3_PRIVACY_PROTOCOLS[0],
              }
            : fillCredPass(credential, { isUnsaved });

        setEditingCredential({
            credential: initialCredential,
            originalCredential: isNew ? null : credential,
            index,
            isNew,
            isUnsaved,
            credentialTypes,
            credentials: currentCredentials,
        });

        setShowDialog(true);
    };

    const handleNewClick = () => {
        handleEditCredential();
    };

    const handleDialogClose = (result) => {
        setShowDialog(false);
        if (!result) return;
        const { isNew, index } = editingCredential;
        if (isNew) {
            onChange([...credentials, result]);
        } else {
            const newList = [...credentials];
            newList[index] = result;
            onChange(newList);
        }
    };
    return (
        <FormSection title={<Trans>Credentials</Trans>}>
            <NgpFormButton onClick={handleNewClick} className={classes.credentialButton}>
                {t`CREATE CREDENTIAL`}
            </NgpFormButton>
            <div className={classes.credentialCard}>
                <div style={{ flexBasis: '50%' }}>
                    <Tabs className={classes.credentialTabs} value={selectedCredentialTab} onChange={handleTabChange}>
                        {_.map(CRED_TABS_META, (value, key) => (
                            <Tab key={key} label={t`${value.label}`} value={key} />
                        ))}
                    </Tabs>
                    <CredentialDragPreview />
                    <List className={`${classes.credentialList} ${isDragActive ? classes.credentialListDragging : ''}`}>
                        <ListSubheader className={classes.credentialListBanner}>
                            <Trans>Use the drag handler icon below to prioritize credentials</Trans>
                        </ListSubheader>
                        {visibleCredentials.map(({ cred, index }) => (
                            <CredentialRow
                                key={cred[INTERNAL_ID_FIELD] || cred._id || index}
                                cred={cred}
                                index={index}
                                move={moveItem}
                                remove={deleteCredential}
                                onEdit={handleEditCredential}
                            />
                        ))}
                    </List>
                    <CredentialDialog
                        showDialog={showDialog}
                        setShowDialog={handleDialogClose}
                        {...editingCredential}
                    />
                </div>
            </div>
        </FormSection>
    );
}
